import { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown } from '../../icons/wp-icons';
import { getAllKunden } from '../../api/kundenApi';
import { getAllBenutzer } from '../../api/benutzerApi';
import { fahrzeugApi } from '../../api/fahrzeugApi';
import { werkzeugApi } from '../../api/werkzeugApi';

function TerminForm({ termin, preselectedKunde, onSubmit, onCancel, isLoading }) {
  const [kunden, setKunden] = useState([]);
  const [loadingKunden, setLoadingKunden] = useState(true);
  const [isPrivat, setIsPrivat] = useState(!termin?.kundeId && !preselectedKunde);
  const [benutzerList, setBenutzerList] = useState([]);

  // Kundensuche State
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    kundeId: '',
    titel: '',
    datum: '',
    uhrzeit: '09:00',
    dauerMinuten: 60,
    ort: '',
    beschreibung: '',
    farbe: '#057867',
    bearbeiterId: ''
  });

  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [werkzeuge, setWerkzeuge] = useState([]);
  const [fahrzeugIds, setFahrzeugIds] = useState([]);
  const [werkzeugIds, setWerkzeugIds] = useState([]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadKunden();
  }, []);

  useEffect(() => {
    if (termin) {
      // Prüfe ob ein gültiges Datum vorhanden ist
      let datumStr = '';
      if (termin.datum) {
        const terminDatum = new Date(termin.datum);
        // Prüfe ob das Datum gültig ist
        if (!isNaN(terminDatum.getTime())) {
          datumStr = terminDatum.toISOString().split('T')[0];
        }
      }

      setFormData(prev => ({
        ...prev,
        kundeId: termin.kundeId || prev.kundeId || '',
        titel: termin.titel || prev.titel || '',
        datum: datumStr || prev.datum || '',
        uhrzeit: termin.uhrzeit || prev.uhrzeit || '09:00',
        dauerMinuten: termin.dauerMinuten || prev.dauerMinuten || 60,
        ort: termin.ort || prev.ort || '',
        beschreibung: termin.beschreibung || prev.beschreibung || '',
        farbe: termin.farbe || prev.farbe || '#057867',
        bearbeiterId: termin.bearbeiterId || prev.bearbeiterId || ''
      }));
      setIsPrivat(!termin.kundeId);

      // Wenn ein Kunde vorhanden ist, setze selectedKunde
      if (termin.kundeId && termin.kunde) {
        setSelectedKunde(termin.kunde);
      }

      // Ressourcen aus bestehender Junction-Liste hydrieren
      if (Array.isArray(termin.fahrzeuge)) {
        setFahrzeugIds(termin.fahrzeuge.map(f => f.fahrzeugId || f.fahrzeug?.id).filter(Boolean));
      }
      if (Array.isArray(termin.werkzeuge)) {
        setWerkzeugIds(termin.werkzeuge.map(w => w.werkzeugId || w.werkzeug?.id).filter(Boolean));
      }
    }
  }, [termin]);

  // Schließe Dropdown wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Wenn Kunden geladen sind und ein Termin mit kundeId existiert, setze selectedKunde
  useEffect(() => {
    if (termin?.kundeId && kunden.length > 0 && !selectedKunde) {
      const kunde = kunden.find(k => k.id === termin.kundeId);
      if (kunde) {
        setSelectedKunde(kunde);
      }
    }
  }, [kunden, termin]);

  // Wenn ein preselectedKunde übergeben wird, setze ihn als ausgewählten Kunden
  useEffect(() => {
    if (preselectedKunde && !selectedKunde) {
      setSelectedKunde(preselectedKunde);
      setFormData(prev => ({ ...prev, kundeId: preselectedKunde.id }));
      setIsPrivat(false);
    }
  }, [preselectedKunde]);

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);
    } catch (error) {
      console.error('Error loading kunden:', error);
    } finally {
      setLoadingKunden(false);
    }
  };

  // Mitarbeiter-Liste (aktive Benutzer, inkl. loginAktiv=false) für Zuweisung laden
  useEffect(() => {
    getAllBenutzer({ aktiv: 'true' })
      .then(data => setBenutzerList(data || []))
      .catch(err => console.error('Mitarbeiter-Liste konnte nicht geladen werden:', err));
  }, []);

  // Fahrzeuge + Werkzeuge für Ressourcen-Picker laden
  useEffect(() => {
    fahrzeugApi.getAll()
      .then(data => setFahrzeuge(data || []))
      .catch(err => console.error('Fahrzeug-Liste konnte nicht geladen werden:', err));
    werkzeugApi.getAll()
      .then(data => setWerkzeuge(data || []))
      .catch(err => console.error('Werkzeug-Liste konnte nicht geladen werden:', err));
  }, []);

  const toggleFahrzeug = (id) => {
    setFahrzeugIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleWerkzeug = (id) => {
    setWerkzeugIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.datum) {
      newErrors.datum = 'Datum ist erforderlich';
    }

    if (!formData.uhrzeit) {
      newErrors.uhrzeit = 'Uhrzeit ist erforderlich';
    }

    if (isPrivat && !formData.titel.trim()) {
      newErrors.titel = 'Titel ist für Privat-Termine erforderlich';
    }

    if (!isPrivat && !formData.kundeId) {
      newErrors.kundeId = 'Bitte wählen Sie einen Kunden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePrivatChange = (privat) => {
    setIsPrivat(privat);
    if (privat) {
      setFormData(prev => ({ ...prev, kundeId: '' }));
      setSelectedKunde(null);
      setKundenSuche('');
    } else {
      setFormData(prev => ({ ...prev, titel: '' }));
    }
  };

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  // Kunde auswählen
  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setFormData(prev => ({
      ...prev,
      kundeId: kunde.id,
      // Wenn der Kunde eine Adresse hat und das Ort-Feld noch leer ist, fülle es automatisch
      ort: kunde.adresse && !prev.ort ? kunde.adresse : prev.ort
    }));
    setKundenSuche('');
    setShowKundenDropdown(false);
    if (errors.kundeId) {
      setErrors(prev => ({ ...prev, kundeId: '' }));
    }
  };

  // Kundenauswahl entfernen
  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setFormData(prev => ({ ...prev, kundeId: '' }));
    setKundenSuche('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        datum: formData.datum,
        uhrzeit: formData.uhrzeit,
        dauerMinuten: parseInt(formData.dauerMinuten),
        ort: formData.ort || null,
        beschreibung: formData.beschreibung || null,
        farbe: formData.farbe,
        bearbeiterId: formData.bearbeiterId || null,
        fahrzeugIds,
        werkzeugIds
      };

      if (isPrivat) {
        submitData.titel = formData.titel;
        submitData.kundeId = null;
      } else {
        submitData.kundeId = formData.kundeId;
        submitData.titel = null;
      }

      onSubmit(submitData);
    }
  };

  const dauerOptionen = [
    { value: 15, label: '15 Minuten' },
    { value: 30, label: '30 Minuten' },
    { value: 45, label: '45 Minuten' },
    { value: 60, label: '1 Stunde' },
    { value: 90, label: '1,5 Stunden' },
    { value: 120, label: '2 Stunden' },
    { value: 180, label: '3 Stunden' },
    { value: 240, label: '4 Stunden' }
  ];

  const farbOptionen = [
    { value: '#057867', label: 'Petrol' },
    { value: '#3b82f6', label: 'Blau' },
    { value: '#8b5cf6', label: 'Violett' },
    { value: '#ec4899', label: 'Pink' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#10b981', label: 'Grün' },
    { value: '#6b7280', label: 'Grau' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 overflow-x-hidden px-1 -mx-1">
      {/* Typ-Auswahl: Kunde oder Privat */}
      <div>
        <label className="block text-sm font-medium mb-2">Termintyp</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handlePrivatChange(false)}
            className={`flex-1 py-2.5 px-3 sm:px-4 rounded-lg font-medium transition text-sm sm:text-base ${
              !isPrivat
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            disabled={isLoading}
          >
            Kunde
          </button>
          <button
            type="button"
            onClick={() => handlePrivatChange(true)}
            className={`flex-1 py-2.5 px-3 sm:px-4 rounded-lg font-medium transition text-sm sm:text-base ${
              isPrivat
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            disabled={isLoading}
          >
            Privat
          </button>
        </div>
      </div>

      {/* Kunde auswählen mit Suchfunktion */}
      {!isPrivat && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Kunde <span className="text-danger">*</span>
          </label>

          <div className="relative" ref={kundenDropdownRef}>
            {/* Ausgewählter Kunde oder Suchfeld */}
            {selectedKunde ? (
              <div className={`flex items-center justify-between w-full px-3 sm:px-4 py-2.5 border rounded-lg bg-gray-50 ${
                errors.kundeId ? 'border-danger' : 'border-border'
              }`}>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sm sm:text-base block truncate">{selectedKunde.name}</span>
                  {selectedKunde.email && (
                    <span className="text-text-muted text-xs sm:text-sm block truncate">{selectedKunde.email}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleKundeRemove}
                  className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={kundenSuche}
                  onChange={(e) => {
                    setKundenSuche(e.target.value);
                    setShowKundenDropdown(true);
                  }}
                  onFocus={() => setShowKundenDropdown(true)}
                  placeholder="Kunde suchen..."
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
                    errors.kundeId ? 'border-danger' : 'border-border'
                  }`}
                  disabled={isLoading || loadingKunden}
                />
                <button
                  type="button"
                  onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  disabled={isLoading || loadingKunden}
                >
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}

            {/* Dropdown Liste */}
            {showKundenDropdown && !selectedKunde && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {loadingKunden ? (
                  <div className="px-4 py-3 text-text-muted text-center">
                    Lädt Kunden...
                  </div>
                ) : gefilterteKunden.length === 0 ? (
                  <div className="px-4 py-3 text-text-muted text-center">
                    {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                  </div>
                ) : (
                  gefilterteKunden.map(kunde => (
                    <button
                      key={kunde.id}
                      type="button"
                      onClick={() => handleKundeSelect(kunde)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                    >
                      <div className="font-medium">{kunde.name}</div>
                      <div className="text-sm text-text-muted flex gap-3">
                        {kunde.email && <span>{kunde.email}</span>}
                        {kunde.telefon && <span>{kunde.telefon}</span>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {errors.kundeId && (
            <p className="text-danger text-sm mt-1">{errors.kundeId}</p>
          )}
        </div>
      )}

      {/* Privat-Termin Titel */}
      {isPrivat && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Titel <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="titel"
            value={formData.titel}
            onChange={handleChange}
            className={`w-full px-3 sm:px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
              errors.titel ? 'border-danger' : 'border-border'
            }`}
            placeholder="z.B. Arzttermin..."
            disabled={isLoading}
          />
          {errors.titel && (
            <p className="text-danger text-sm mt-1">{errors.titel}</p>
          )}
        </div>
      )}

      {/* Datum und Uhrzeit */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Datum <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            name="datum"
            value={formData.datum}
            onChange={handleChange}
            className={`w-full px-3 sm:px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
              errors.datum ? 'border-danger' : 'border-border'
            }`}
            disabled={isLoading}
          />
          {errors.datum && (
            <p className="text-danger text-sm mt-1">{errors.datum}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Uhrzeit <span className="text-danger">*</span>
          </label>
          <input
            type="time"
            name="uhrzeit"
            value={formData.uhrzeit}
            onChange={handleChange}
            className={`w-full px-3 sm:px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
              errors.uhrzeit ? 'border-danger' : 'border-border'
            }`}
            disabled={isLoading}
          />
          {errors.uhrzeit && (
            <p className="text-danger text-sm mt-1">{errors.uhrzeit}</p>
          )}
        </div>
      </div>

      {/* Dauer und Farbe */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Dauer</label>
          <select
            name="dauerMinuten"
            value={formData.dauerMinuten}
            onChange={handleChange}
            className="w-full px-3 sm:px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
            disabled={isLoading}
          >
            {dauerOptionen.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Farbe</label>
          <div className="flex flex-wrap gap-2">
            {farbOptionen.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, farbe: opt.value }))}
                className={`w-8 h-8 sm:w-7 sm:h-7 rounded-full border-2 transition shrink-0 ${
                  formData.farbe === opt.value ? 'border-gray-800 scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: opt.value }}
                title={opt.label}
                disabled={isLoading}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Ort */}
      <div>
        <label className="block text-sm font-medium mb-2">Ort</label>
        <input
          type="text"
          name="ort"
          value={formData.ort}
          onChange={handleChange}
          className="w-full px-3 sm:px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
          placeholder="Adresse eingeben..."
          disabled={isLoading}
        />
      </div>

      {/* Zugewiesen an */}
      <div>
        <label className="block text-sm font-medium mb-2">Zugewiesen an</label>
        <select
          name="bearbeiterId"
          value={formData.bearbeiterId}
          onChange={handleChange}
          disabled={isLoading}
          className="w-full px-3 sm:px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base bg-white"
        >
          <option value="">— Nicht zugewiesen —</option>
          {benutzerList.map(b => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {/* Ressourcen — Fahrzeuge */}
      {fahrzeuge.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Fahrzeuge
            {fahrzeugIds.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({fahrzeugIds.length} ausgewählt)
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {fahrzeuge.map(f => {
              const checked = fahrzeugIds.includes(f.id);
              const disabled = f.status === 'AUSSORTIERT';
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => !disabled && toggleFahrzeug(f.id)}
                  disabled={isLoading || disabled}
                  title={f.status === 'WARTUNG' ? 'In Wartung' : f.status === 'AUSFALL' ? 'Ausgefallen' : ''}
                  className={`px-3 py-1.5 rounded-button text-sm border transition ${
                    checked
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-text border-border hover:border-primary'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {f.kennzeichen} · {f.bezeichnung}
                  {f.status !== 'VERFUEGBAR' && f.status !== 'AUSSORTIERT' && (
                    <span className="ml-1 text-xs">⚠</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Ressourcen — Werkzeuge */}
      {werkzeuge.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Werkzeuge
            {werkzeugIds.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({werkzeugIds.length} ausgewählt)
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {werkzeuge.map(w => {
              const checked = werkzeugIds.includes(w.id);
              const disabled = w.zustand === 'AUSSORTIERT' || w.zustand === 'DEFEKT';
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => !disabled && toggleWerkzeug(w.id)}
                  disabled={isLoading || disabled}
                  title={w.zustand === 'DEFEKT' ? 'Defekt' : ''}
                  className={`px-3 py-1.5 rounded-button text-sm border transition ${
                    checked
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-text border-border hover:border-primary'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {w.bezeichnung}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Beschreibung */}
      <div>
        <label className="block text-sm font-medium mb-2">Beschreibung</label>
        <textarea
          name="beschreibung"
          value={formData.beschreibung}
          onChange={handleChange}
          rows="3"
          className="w-full px-3 sm:px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
          placeholder="Zusätzliche Informationen..."
          disabled={isLoading}
        />
      </div>

      {/* Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
          disabled={isLoading}
        >
          Abbrechen
        </button>
        <button
          type="submit"
          className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Wird gespeichert...' : termin ? 'Aktualisieren' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}

export default TerminForm;
